import { useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { asId } from '@shared/brand';
import type { AreaId, PersonId, SubAreaId } from '@shared/brand';
import { MIN_CONCURRENT_OUT, subAreaColor } from '@domain/entities/Organization';
import type { SubArea } from '@domain/entities/Organization';
import {
  MINIMUM_EMERGENCY_CONTACTS,
  personLeaderLinkId,
  type Person,
  type PersonLeaderLink,
} from '@domain/entities/Person';
import { CURRENT_SCHEMA_VERSION } from '@infrastructure/codecs/base';
import { leadsTeams, ROLE_LABEL, type Role } from '@domain/vocabulary';
import { FIELD_LABEL } from '@domain/entities/DataConfirmation';
import { getServices } from '@infrastructure/services';
import type { PendingAccountMatch } from '@application/ports';
import {
  Avatar,
  Button,
  Chip,
  EmptyState,
  Pagination,
  Spinner,
  cssVars,
  cx,
} from '../../design-system';
import { useAreaName } from '../../hooks/useAreaName';
import { usePagination } from '../../hooks/usePagination';
import { foldForSearch } from '../Requests/candidateSearch';
import { useAccess } from '../../hooks/useAccess';
import { useSession } from '../../providers/SessionProvider';
import { useScopedAreaId } from '../../providers/AreaFocusProvider';
import { useToast } from '../../providers/ToastProvider';
import { reasonFor } from '../../format';
import { SubAreaName } from './SubAreaName';
import { reservedHeight } from './viewportFloor';
import { PersonIntakeModal } from './PersonIntakeModal';
import styles from './AdminScreen.module.css';
import { usePersonLink } from '../../hooks/usePersonLink';
import { ReportedPersonName } from './ReportedPersonName';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Panel administrador — transcribed from
 * `design/reference/screens/administrador.html`.
 *
 * Area policy, pending accounts, and the Personas · Sub áreas · Líderes tabs.
 *
 * Everything here is a privileged mutation and none of it is a client write: each
 * action calls a callable that re-checks authority server-side. Validating an
 * account mints custom claims; assigning a leader changes who may approve
 * someone's absence.
 */

type AdminTab = 'personas' | 'subareas' | 'lideres';

/**
 * Qué mitad de esta pantalla se está viendo.
 *
 * El panel administrador era una sola entrada de menú, «Sub áreas y cuentas», y
 * bajo el esquema de KNPeople es dos módulos: el padrón y las altas son
 * **People**, la estructura del área es **Organization**. La pantalla no se
 * duplicó — se le pasa el alcance y ella decide qué pestañas ofrece.
 *
 * Lo que **no** se partió, y es deliberado: la política de días y las tres
 * alertas se quedan en las dos rutas. Son el estado del área, que es de lo que
 * hablan los dos módulos, y repartirlas dejaría a cada ruta con un tablero
 * incompleto — que es justo lo que la nota de `alerts` defiende no hacer.
 */
export type AdminScope = 'people' | 'organization';

/**
 * Las pestañas de cada alcance, en orden. La primera es la que abre.
 *
 * People tiene una sola, así que su barra de pestañas no se dibuja: un control
 * segmentado de un segmento no es un filtro, es un rótulo que parece pulsable.
 */
const TABS_BY_SCOPE: Readonly<Record<AdminScope, readonly AdminTab[]>> = {
  people: ['personas'],
  organization: ['subareas', 'lideres'],
};

/** "1 día", not "1 días" — these tiles routinely hold a one. */
const days = (n: number): string => `${String(n)} ${n === 1 ? 'día' : 'días'}`;

/**
 * El ancestro que de verdad hace scroll: un panel con `overflow` si lo hay, y
 * si no, el documento. Preguntarlo en vez de suponerlo es lo que hace que esto
 * siga funcionando si mañana el shell cambia de contenedor.
 */
const scrollerOf = (node: HTMLElement): HTMLElement => {
  for (let el = node.parentElement; el !== null; el = el.parentElement) {
    const overflowY = getComputedStyle(el).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
  }
  return document.documentElement;
};

interface LeaderLink {
  readonly personId: PersonId;
  readonly leaderPersonId: PersonId;
  /** What the leader leads them on, e.g. «App 4.2 iOS». */
  readonly projectLabel: string;
}

/**
 * The links query key, spelled once.
 *
 * An optimistic write has to name the same key the query does, and a key that is
 * built in two places is a key that eventually disagrees with itself — the write
 * lands in a cache entry nobody reads and the change simply does not appear.
 */
const linksKeyFor = (areaId: AreaId): readonly unknown[] => ['admin', 'links', areaId];

/**
 * A link the server has not written yet.
 *
 * The id is the one the server derives from the same two ids, so when the refetch
 * lands the real row replaces this one instead of appearing beside it.
 */
const provisionalLink = (
  personId: PersonId,
  leaderPersonId: PersonId,
  areaId: AreaId,
): PersonLeaderLink => {
  const now = getServices().clock.now();
  return {
    id: personLeaderLinkId(personId, leaderPersonId),
    personId,
    leaderPersonId,
    areaId,
    projectLabel: '',
    createdAt: now,
    updatedAt: now,
    schemaVersion: CURRENT_SCHEMA_VERSION,
  };
};

export const AdminScreen = ({ scope }: { scope: AdminScope }): ReactNode => {
  const session = useSession();
  /* El área que toca mirar: la propia, o la enfocada si su alcance es la
     empresa entera. Ver `AreaFocusProvider`. */
  const areaId = useScopedAreaId(session.areaId);
  const { featureOn } = useAccess();
  const areaName = useAreaName();
  const toast = useToast();
  /* Cuál se está cerrando. Por persona y no un booleano: dos clics seguidos en
     dos tarjetas distintas no deben apagarse el uno al otro. */
  const [atendiendo, setAtendiendo] = useState<string | null>(null);
  const tabs = TABS_BY_SCOPE[scope];
  /*
   * `tabs[0]` no puede faltar —los dos alcances declaran al menos una— pero el
   * `noUncheckedIndexedAccess` de este proyecto no lo sabe, y afirmárselo con un
   * `!` sería justo el force-unwrap que las reglas prohíben. El valor por omisión
   * es el mismo que la tabla ya da para People.
   */
  const [chosenTab, setTab] = useState<AdminTab>(tabs[0] ?? 'personas');
  const [intakeOpen, setIntakeOpen] = useState(false);

  /*
   * El alcance manda sobre lo elegido, y no es defensa hipotética: se vio en
   * pantalla.
   *
   * Las dos rutas montan este mismo componente en la misma posición del árbol,
   * así que React reutiliza la instancia al pasar de `/administrador` a
   * `/organizacion` — y el valor inicial de `useState` sólo se lee en el primer
   * montaje. El resultado era «Sub áreas y líderes» encabezando la pestaña
   * Personas: título y pestañas de Organization, nota y contador de People.
   *
   * Se acota al leer y no al escribir a propósito. Un `key` en la ruta también lo
   * arreglaría, pero deja la invariante en `App.tsx`, lejos de quien lee esta
   * pantalla y se pregunta si `tab` puede estar fuera de `tabs`. Aquí no puede.
   */
  const tab = tabs.includes(chosenTab) ? chosenTab : (tabs[0] ?? 'personas');

  /*
   * Un solo buscador para las tres pestañas.
   *
   * Filtra por nombre con el mismo plegado que usa el selector de reemplazo, así
   * que «gamino» encuentra a Gamiño y «rocio» a Rocío. Sobre treinta y siete
   * personas y trece sub áreas, encontrar a alguien no debería ser un ejercicio
   * de lectura.
   */
  const [search, setSearch] = useState('');

  /*
   * Alto que el cuerpo de la pestaña conserva mientras se busca.
   *
   * Al filtrar, la lista pasa de cuarenta filas a tres y la página se queda
   * corta: el navegador no puede sostener el desplazamiento actual porque ya no
   * hay documento debajo, así que lo recorta y la vista salta hacia arriba sola.
   * Es el mismo salto que se siente al teclear cada letra, y no lo pidió nadie.
   *
   * Reservar el hueco lo evita sin tocar el scroll. Se reserva lo justo —hasta
   * el borde inferior de lo que se está viendo, ni un pixel más—, así que el
   * vacío nunca pasa de una pantalla y desaparece al limpiar la búsqueda.
   */
  const bodyRef = useRef<HTMLDivElement>(null);
  const [floor, setFloor] = useState(0);

  /**
   * Mide *antes* de que React vuelva a pintar, con la lista larga todavía en el
   * DOM. Desde un efecto sería tarde: para entonces la página ya encogió y el
   * navegador ya movió la vista.
   */
  const holdViewport = (next: string): void => {
    const body = bodyRef.current;
    if (body === null) return;
    if (next.length === 0) {
      setFloor(0);
      return;
    }
    const scroller = scrollerOf(body);
    const box = body.getBoundingClientRect();
    const scrollerTop =
      scroller === document.documentElement ? 0 : scroller.getBoundingClientRect().top;
    /** Dónde empieza el cuerpo dentro de lo que se ve. Negativo si ya pasó. */
    const offset = box.top - scrollerTop;
    setFloor(
      reservedHeight({
        viewport: scroller.clientHeight,
        offset,
        // Lo que haya después del cuerpo ya sostiene esa parte del alto.
        tail: scroller.scrollHeight - (offset + scroller.scrollTop) - box.height,
      }),
    );
  };

  /**
   * Lo que la gente reportó de su propia ficha al entrar.
   *
   * `listReported` recibe `null` sólo para el superusuario, que es el único a
   * quien la regla se lo permite: para todos los demás la consulta tiene que
   * filtrar por `areaId` o la incumple entera y falla nada más para ellos.
   */
  const reportes = useQuery({
    queryKey: ['admin', 'dataReports', areaId, session.role],
    queryFn: async () => {
      const result = await getServices().dataConfirmations.listReported(
        session.role === 'super' ? null : areaId,
      );
      if (!result.ok) throw new Error('No pudimos cargar los datos reportados.');
      return result.value;
    },
  });

  /* Sólo lo que sigue esperando: un reporte atendido deja de ser trabajo, pero el
     documento se queda —es la prueba de que esa persona revisó su ficha—. */
  const pendientesDeAtender = (reportes.data ?? []).filter(
    (reporte) => reporte.resolvedAt === null,
  );
  /* Las dos bandejas, de diez en diez: en una semana de ingresos se juntan
     decenas de cuentas por validar. Los contadores de arriba siguen diciendo
     cuántas hay en total. */
  const reportesPager = usePagination(pendientesDeAtender);

  const atender = async (personId: string, nombre: string): Promise<void> => {
    setAtendiendo(personId);
    const result = await getServices().dataConfirmations.resolve(asId<'PersonId'>(personId));
    setAtendiendo(null);
    if (!result.ok) {
      toast.bad(`No pudimos marcarlo como atendido.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(`Reporte de ${nombre} atendido.`);
    void reportes.refetch();
  };

  /*
   * Quien ve todas las áreas ve también las cuentas que todavía no tienen área.
   *
   * Son las que crea el portal cuando alguien entra con Google y no hay ficha con
   * su correo, y nacen con `areaId: ''`. Esta cola sólo pedía las del área a la
   * vista, así que **nadie las veía**: el 1 oct 2026 había cuatro esperando desde
   * la mañana —gente con ficha activa a la que no se le reconoció el correo—. Las
   * reglas sólo dejan leerlas a `global` y `super`, que son quienes las reparten.
   */
  const seesUnassigned = session.role === 'global' || session.role === 'super';
  const pending = useQuery({
    queryKey: ['admin', 'pending', areaId, seesUnassigned],
    queryFn: async () => {
      const [delArea, sinArea] = await Promise.all([
        getServices().people.list({ areaId: areaId, status: 'pending' }, { limit: 50 }),
        seesUnassigned
          ? getServices().people.list({ areaId: asId<'AreaId'>(''), status: 'pending' }, { limit: 50 })
          : Promise.resolve(null),
      ]);
      /*
       * Sin filtrar por sub área, y ésta es la que más importaba: una cuenta recién
       * registrada **no tiene** sub área todavía —se la pone quien la valida—, así
       * que descartarlas dejaba fuera de «Cuentas por validar» justo a las que
       * llegan por el camino normal.
       */
      return [
        ...(delArea.ok ? delArea.value.items : []),
        ...(sinArea !== null && sinArea.ok ? sinArea.value.items : []),
      ];
    },
  });

  const subAreas = useQuery({
    queryKey: ['admin', 'subAreas', areaId],
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(areaId);
      return result.ok ? result.value : [];
    },
  });

  const people = useQuery({
    queryKey: ['admin', 'people', areaId],
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: areaId, status: 'active' },
        { limit: 200 },
      );
      /*
       * Quien no tiene sub área también sale.
       *
       * Se descartaban, sin motivo escrito y desde el commit de paridad con el
       * prototipo, y el efecto era una trampa: la persona sin sub área desaparecía
       * de la única pantalla donde se le asigna una. Lo destapó el primer traslado
       * a un área que se estrenaba — dos personas en la ficha del área y cero en
       * la lista. La fila ya sabe pintar «Sin sub área»; no hacía falta esconderla.
       */
      return result.ok ? result.value.items : [];
    },
  });

  const links = useQuery({
    queryKey: linksKeyFor(areaId),
    queryFn: async () => {
      const result = await getServices().leaders.listByArea(areaId);
      return result.ok ? result.value : [];
    },
  });

  /** Requests one signature short of approval — a server-side count. */
  const awaitingSecond = useQuery({
    queryKey: ['admin', 'awaitingSecond', areaId],
    queryFn: async () => {
      const result = await getServices().requests.count({
        areaId: areaId,
        statuses: ['Visto bueno'],
      });
      return result.ok ? result.value : 0;
    },
  });

  const rules = session.config.rules;
  const allPeople = people.data ?? [];
  const allLinks = links.data ?? [];
  const allSubAreas = subAreas.data ?? [];

  /*
   * Lo filtrado alimenta el cuerpo de la pestaña, no sus contadores.
   *
   * Los números de las pestañas rotulan la estructura del área — cuánta gente
   * hay, cuántas sub áreas — y que cambiaran al teclear convertiría una etiqueta
   * en un resultado de búsqueda.
   */
  const needle = foldForSearch(search);
  const shownPeople =
    needle.length === 0
      ? allPeople
      : allPeople.filter((p) => foldForSearch(p.displayName).includes(needle));
  const shownSubAreas =
    needle.length === 0
      ? allSubAreas
      : allSubAreas.filter((sa) => foldForSearch(sa.name).includes(needle));
  const pendingList = pending.data ?? [];
  const pendingPager = usePagination(pendingList);

  /*
   * Buscar acota **las filas que se listan**, no los datos con los que la pestaña
   * resuelve nombres y roles.
   *
   * La distinción no era ninguna hasta que se notó: las tres pestañas recibían
   * `shownPeople` como su único padrón y lo usaban para dos trabajos distintos.
   * En Líderes eso significaba que teclear «Adri» borraba del mapa a todos los
   * demás, así que los líderes asignados de alguien aparecían como «Desconocido» y
   * la lista entera se vaciaba — el filtro no ocultaba filas, deshacía la
   * estructura del área.
   *
   * Se pasan los dos: el padrón completo para resolver, y este conjunto para
   * decidir qué se enseña.
   */
  const shownPersonIds = new Set(shownPeople.map((p) => p.id));
  const searching = needle.length > 0;
  const clearSearch = (): void => {
    setSearch('');
  };

  /*
   * People whose requests would have nobody to escalate to.
   *
   * A leader is not one of them — nobody sits above them in the area by design —
   * and neither is RR.HH. or the superuser, who file no requests at all. Counting
   * those made the alert read seven when nothing was actually wrong.
   */
  const areaLeaderIds = new Set(allPeople.filter((p) => leadsTeams(p.role)).map((p) => p.id));
  const withoutLeader = allPeople.filter(
    (p) =>
      p.role !== 'admin' &&
      p.role !== 'super' &&
      !areaLeaderIds.has(p.id) &&
      !allLinks.some((l) => l.personId === p.id),
  );

  /*
   * Transcribed from the prototype's `policies`.
   *
   * These are the settings an area administrator has to hold in mind while
   * moving people around below — how many days each person gets, how many
   * leaders sign, how long the balance lasts. The global minimums that bound
   * them belong to the superuser, which is what the hint beside the heading
   * says.
   */
  const seniority = rules.seniority;
  const brackets = [...seniority.brackets].sort((a, b) => a.fromYear - b.fromYear);
  const leaderCount = allPeople.filter((p) => leadsTeams(p.role)).length;
  const dualOn = featureOn('doblevb');

  const policies: readonly { label: string; value: string }[] = [
    {
      label: 'Días por periodo (empresa)',
      value: days(
        seniority.enabled
          ? (brackets[brackets.length - 1]?.days ?? 0) + seniority.additionalDays
          : seniority.fixedDays,
      ),
    },
    {
      label: 'Tabla por antigüedad',
      value: seniority.enabled
        ? `${String(brackets[0]?.days ?? 0)} → ${String(
            brackets[brackets.length - 1]?.days ?? 0,
          )} días`
        : `Apagada · ${String(seniority.fixedDays)} días fijos`,
    },
    {
      label: 'Líderes en el área',
      value: `${String(leaderCount)} · doble visto bueno ${dualOn ? 'activo' : 'inactivo'}`,
    },
    {
      label: 'Vigencia para gozar los días',
      value: `${String(rules.expiryMonths)} ${rules.expiryMonths === 1 ? 'mes' : 'meses'}`,
    },
    { label: 'Mínimo por solicitud', value: days(rules.minimumRequestDays) },
    { label: 'Aviso previo', value: days(rules.noticeDays) },
    { label: 'Sub áreas activas', value: String(allSubAreas.length) },
  ];

  /**
   * Only the counts that are actually non-zero. An alert card showing "0" is
   * noise that trains people to ignore the row.
   */
  /*
   * The three alerts, always shown — including at zero.
   *
   * This is the opposite choice from the home screen's «Por atender hoy», and
   * deliberately so: there a zero row is noise among six, here the three are a
   * fixed dashboard an administrator checks, and a missing card would read as
   * "not measured" rather than "nothing pending".
   */
  const alerts = [
    {
      n: pendingList.length,
      title: 'Cuentas por validar',
      note: 'Registros nuevos que aún no pueden solicitar días',
      color: 'var(--color-warning)',
      tint: 'var(--color-warning-bg)',
    },
    {
      n: withoutLeader.length,
      title: 'Personas sin líder',
      note: 'Las firma quien administra el área, no su equipo',
      color: 'var(--color-danger)',
      tint: 'var(--color-danger-bg)',
    },
    {
      n: awaitingSecond.data ?? 0,
      title: 'Esperando 2º visto bueno',
      note: 'Aprobadas por un líder, falta la segunda firma',
      color: 'var(--color-brand)',
      tint: 'var(--color-brand-bg)',
    },
  ];

  // Same definition the Líderes tab uses: the role, not the links.
  const leaderTotal = allPeople.filter((p) => leadsTeams(p.role)).length;

  const sectionNote =
    tab === 'personas'
      ? 'Mueve a cada persona de sub área y asígnale uno o varios líderes. Con dos o más líderes, sus vacaciones piden doble visto bueno.'
      : tab === 'subareas'
        ? 'Cuántas personas pueden estar fuera al mismo tiempo en cada sub área y quién responde por ella. Quién firma las vacaciones se asigna por persona, en Personas y cuentas.'
        : 'Quién puede aprobar en el área y cuándo se exige un segundo visto bueno.';

  const sectionCount =
    tab === 'personas'
      ? `${String(allPeople.length)} ${
          allPeople.length === 1 ? 'persona en el área' : 'personas en el área'
        }`
      : tab === 'subareas'
        ? `${String(allSubAreas.length)} sub áreas`
        : `${String(leaderTotal)} ${leaderTotal === 1 ? 'líder' : 'líderes'}`;

  /*
   * El buscador baja hasta el cuerpo de la pestaña, pegado a la lista.
   *
   * Arriba quedaba entre dos hileras de filtros —las pestañas y los chips de sub
   * área— y parecía filtrar las tres cosas. Pegado a las filas se lee por lo que
   * es: el último filtro antes de la lista, después de haber elegido sub área.
   */
  const searchBox = (
    <div className={styles.searchRow}>
      <input
        type="search"
        className={styles.searchInput}
        placeholder="Buscar por nombre…"
        aria-label="Buscar en la estructura del área"
        value={search}
        onChange={(event) => {
          holdViewport(event.target.value);
          setSearch(event.target.value);
        }}
      />
      {/*
        * La salida, escrita y no sólo implícita.
        *
        * El `type="search"` trae una ✕ nativa, y no basta: sólo la dibuja WebKit,
        * mide unos pocos píxeles y no dice qué hace. Cuando el filtro deja la lista
        * vacía —que es justo cuando hace falta— la pantalla no ofrecía ninguna
        * manera de volver, y quien buscó «test3» se quedaba mirando un panel vacío
        * sin nada que pulsar.
        */}
      {search.length > 0 && (
        <button type="button" className={styles.searchClear} onClick={clearSearch}>
          Quitar filtro
        </button>
      )}
      {needle.length > 0 && (
        <span className={styles.searchCount}>
          {tab === 'subareas'
            ? `${String(shownSubAreas.length)} de ${String(allSubAreas.length)} sub áreas`
            : `${String(shownPeople.length)} de ${String(allPeople.length)} personas`}
        </span>
      )}
    </div>
  );

  return (
    <div className={styles.screen}>
      <div className={styles.screenHead}>
        <div>
          <PageTitle className={styles.title}>
            {scope === 'people' ? 'Personas y cuentas' : 'Sub áreas y líderes'}
          </PageTitle>
          <p className={styles.note}>
            Área {areaName} · {allPeople.length}{' '}
            {allPeople.length === 1 ? 'persona' : 'personas'}, {allSubAreas.length} sub áreas. Lo
            urgente primero.
          </p>
        </div>

        {/* El alta vive con People. Es la acción que provoca una llegada; validar
            reacciona a una, y por eso no comparten botón ni panel. */}
        {scope === 'people' && (
          <Button
            variant="primary"
            onClick={() => {
              setIntakeOpen(true);
            }}
          >
            Nuevo Registro
          </Button>
        )}
      </div>

      {intakeOpen && (
        <PersonIntakeModal
          onClose={() => {
            setIntakeOpen(false);
          }}
          onCreated={async () => {
            await people.refetch();
          }}
        />
      )}

      {/* Policy first, as the design orders it: it is the context for everything
          else on the screen. */}
      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>Política de días del área</h2>
          <span className={styles.panelHint}>
            Los mínimos globales los define el superusuario
          </span>
        </div>

        <div className={styles.policyGrid}>
          {policies.map((policy) => (
            <div key={policy.label} className={styles.policyTile}>
              <div className={styles.policyLabel}>{policy.label}</div>
              <div className={styles.policyValue}>{policy.value}</div>
            </div>
          ))}
        </div>
      </section>

      {alerts.length > 0 && (
        <div className={styles.alerts}>
          {alerts.map((alert) => (
            <div
              key={alert.title}
              className={styles.alert}
              style={cssVars({
                '--alert-color': alert.color,
                '--alert-tint': alert.tint,
                '--alert-border': alert.n > 0 ? alert.color : '#cbd5e1',
                '--alert-number-bg':
                  alert.n > 0
                    ? `linear-gradient(140deg, ${alert.color}, color-mix(in srgb, ${alert.color} 78%, #314053))`
                    : '#eef2f7',
                '--alert-number-fg': alert.n > 0 ? '#fff' : 'var(--color-text-tertiary-alt)',
              })}
            >
              <span className={styles.alertNumber}>{alert.n}</span>
              <span className={styles.alertText}>
                <span className={styles.alertTitle}>{alert.title}</span>
                <span className={styles.alertNote}>{alert.note}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Las altas son People. Organization administra la estructura, no quién
          entra a ella. */}
      {scope === 'people' && (
      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <div className={styles.panelHeadText}>
            <h2 className={styles.panelTitle}>Cuentas por validar</h2>
            <p className={styles.panelNote}>
              Cada registro nuevo con dominio corporativo espera tu alta para poder solicitar
              días.
            </p>
          </div>
          <Chip
            background={
              pendingList.length > 0 ? 'var(--color-warning-bg)' : 'var(--color-success-bg)'
            }
            foreground={
              pendingList.length > 0 ? 'var(--color-warning-ink)' : 'var(--color-success-ink)'
            }
          >
            {pendingList.length > 0
              ? `${String(pendingList.length)} esperando alta`
              : 'Todo al día'}
          </Chip>
        </div>

        {pending.isLoading ? (
          <Spinner label="Cargando" />
        ) : pendingList.length === 0 ? (
          <EmptyState
            glyph="✓"
            title="Nada pendiente"
            note="Cuando alguien nuevo entre con su correo corporativo, aparecerá aquí."
          />
        ) : (
          <ul className={styles.pendingGrid}>
            {pendingPager.items.map((person) => (
              <PendingAccountCard
                key={person.id}
                person={person}
                subAreas={allSubAreas}
                onDone={async () => {
                  await pending.refetch();
                  await people.refetch();
                }}
              />
            ))}
          </ul>
        )}

        <Pagination pager={pendingPager} noun="cuentas" />
      </section>
      )}

      {/*
        Lo que la gente dijo que está mal en su propia ficha.
        Va debajo de «Cuentas por validar» porque es el mismo trabajo —dejar el
        padrón utilizable— y encima de la lista de personas, que es donde se
        termina yendo a arreglarlo.
      */}
      {scope === 'people' && (
        <section className={styles.panel}>
          <div className={styles.panelHead}>
            <div className={styles.panelHeadText}>
              <h2 className={styles.panelTitle}>Datos reportados</h2>
              <p className={styles.panelNote}>
                Quien entra al portal confirma su ficha. Esto es lo que no coincidía, con el
                valor que la persona dice que debería tener.
              </p>
            </div>
            <Chip
              background={
                pendientesDeAtender.length > 0
                  ? 'var(--color-warning-bg)'
                  : 'var(--color-success-bg)'
              }
              foreground={
                pendientesDeAtender.length > 0
                  ? 'var(--color-warning-ink)'
                  : 'var(--color-success-ink)'
              }
            >
              {pendientesDeAtender.length > 0
                ? `${String(pendientesDeAtender.length)} por atender`
                : 'Todo al día'}
            </Chip>
          </div>

          {reportes.isLoading ? (
            <Spinner label="Cargando" />
          ) : pendientesDeAtender.length === 0 ? (
            <EmptyState
              glyph="✓"
              title="Nada reportado"
              note="Cuando alguien marque un dato que no coincide con su ficha, aparecerá aquí."
            />
          ) : (
            <ul className={styles.reportList}>
              {reportesPager.items.map((reporte) => (
                <li key={reporte.personId} className={styles.reportCard}>
                  <div className={styles.reportHead}>
                    <ReportedPersonName
                      personId={reporte.personId}
                      name={reporte.personName}
                      className={styles.reportWho}
                    />
                    <Button
                      variant="secondary"
                      small
                      busy={atendiendo === reporte.personId}
                      onClick={() => {
                        void atender(reporte.personId, reporte.personName);
                      }}
                    >
                      Marcar atendido
                    </Button>
                  </div>
                  <ul className={styles.reportFields}>
                    {reporte.corrections.map((correccion) => (
                      <li key={correccion.field} className={styles.reportField}>
                        <span className={styles.reportLabel}>{FIELD_LABEL[correccion.field]}</span>
                        {/* Lo que decía y lo que debería decir, uno al lado del
                            otro: es la comparación que hay que hacer para
                            corregir, y separarla en dos renglones obliga a
                            recordar el primero. */}
                        <span className={styles.reportSays}>
                          {correccion.shown.length > 0 ? correccion.shown : 'Sin registrar'}
                        </span>
                        <span className={styles.reportArrow} aria-hidden="true">
                          →
                        </span>
                        <span className={styles.reportShould}>{correccion.correct}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}

          <Pagination pager={reportesPager} noun="reportes" />
        </section>
      )}

      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <div className={styles.panelHeadText}>
            <h2 className={styles.panelTitle}>
              {scope === 'people' ? 'Personas del área' : 'Estructura del área'}
            </h2>
            {/* The note and the count follow the open tab, because each answers
                a different question about the same structure. */}
            <p className={styles.panelNote}>{sectionNote}</p>
          </div>
          <Chip background="var(--color-brand-bg)" foreground="var(--color-brand-ink)">
            {sectionCount}
          </Chip>
        </div>

        {/* Una sola pestaña no es un control segmentado, es un rótulo con aspecto
            de botón. El alcance People tiene una, así que no dibuja la pista. */}
        {tabs.length > 1 && (
        <div className={styles.tabTrack} role="tablist" aria-label="Secciones de la estructura">
          {(
            [
              { id: 'personas', label: 'Personas', count: allPeople.length },
              { id: 'subareas', label: 'Sub áreas', count: allSubAreas.length },
              { id: 'lideres', label: 'Líderes', count: leaderTotal },
            ] as const
          )
            .filter((option) => tabs.includes(option.id))
            .map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={tab === option.id}
              className={cx(styles.tab, tab === option.id && styles.tabActive)}
              onClick={() => {
                setTab(option.id);
                // El hueco reservado era del alto de la otra pestaña.
                setFloor(0);
              }}
            >
              {option.label}
              <span className={styles.tabCount}>{option.count}</span>
            </button>
          ))}
        </div>
        )}

        <div ref={bodyRef} style={floor > 0 ? { minHeight: `${String(floor)}px` } : undefined}>
          {tab === 'personas' && (
            <PeopleTab
              people={shownPeople}
              roster={allPeople}
              subAreas={allSubAreas}
              links={allLinks}
              loading={people.isLoading}
              search={searchBox}
              searching={searching}
              needle={needle}
              onClearSearch={clearSearch}
              onChanged={async () => {
                await people.refetch();
                await links.refetch();
              }}
            />
          )}
          {tab === 'subareas' && (
            <SubAreasTab
              subAreas={shownSubAreas}
              people={allPeople}
              search={searchBox}
              searching={searching}
              onClearSearch={clearSearch}
              onChanged={async () => {
                await subAreas.refetch();
              }}
            />
          )}
          {tab === 'lideres' && (
            /* El padrón entero, y aparte quién pasa el filtro. Ver la nota junto a
               `shownPersonIds`: esta pestaña resuelve nombres y candidatos contra
               el área completa, y filtrárselo la deshacía. */
            <LeadersTab
              people={allPeople}
              shownPersonIds={shownPersonIds}
              links={allLinks}
              subAreas={allSubAreas}
              dualOn={featureOn('doblevb')}
              search={searchBox}
              onClearSearch={clearSearch}
              onChanged={async () => {
                await links.refetch();
                await people.refetch();
                await subAreas.refetch();
              }}
            />
          )}
        </div>
      </section>
    </div>
  );
};

/** Validating an account: assign a sub area and a role, then mint the claims. */
const PendingAccountCard = ({
  person,
  subAreas,
  onDone,
}: {
  person: Person;
  subAreas: readonly SubArea[];
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [subAreaId, setSubAreaId] = useState('');
  const [role, setRole] = useState<Role>('empleado');
  const [busy, setBusy] = useState(false);
  /*
   * El sueldo se pide aquí, que es donde pasó a ser obligatorio.
   *
   * Sólo si esta persona no lo trae ya: quien capturó el alta con permiso pudo
   * ponerlo, y volver a pedirlo sería pedir dos veces el dato más sensible de la
   * base. Se pregunta al servidor, no se adivina por el rol de quien mira.
   */
  const { holds } = useAccess();
  const sueldo = useQuery({
    queryKey: ['pending', 'salary', person.id],
    enabled: holds('ver_salario'),
    queryFn: async () => {
      const result = await getServices().salaries.getFor(person.id);
      return result.ok ? result.value : null;
    },
  });
  const [monthlySalary, setMonthlySalary] = useState('');
  const monthlyCents = Math.round(Number(monthlySalary.replace(/[^0-9.]/g, '')) * 100);
  const faltaSueldo = holds('editar_salario') && sueldo.data === null && !sueldo.isPending;

  /*
   * «Parece ser…»: las fichas activas que nunca han entrado y se parecen a esta
   * cuenta —mismo nombre, nombre parecido o el correo con una letra de menos—.
   * El 1 oct 2026 las cuatro cuentas pendientes eran de gente con ficha, y
   * validarlas las habría dejado activas dos veces.
   */
  const matches = useQuery({
    queryKey: ['pending', 'matches', person.id],
    queryFn: async () => {
      const result = await getServices().people.pendingMatches(person.id);
      return result.ok ? result.value : [];
    },
  });
  const parecidas = matches.data ?? [];
  const [otraPersona, setOtraPersona] = useState(false);
  const [merging, setMerging] = useState(false);

  const merge = async (match: PendingAccountMatch): Promise<void> => {
    setMerging(true);
    const result = await getServices().people.mergePendingAccount({
      pendingId: person.id,
      personId: match.personId,
    });
    setMerging(false);
    if (!result.ok) {
      toast.bad(`No se pudo unir la cuenta.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(`${person.email} ya entra a la ficha de ${match.displayName}.`);
    await onDone();
  };

  const validate = async (): Promise<void> => {
    if (subAreaId.length === 0) return;
    setBusy(true);

    const result = await getServices().people.activateAccount({
      personId: person.id,
      subAreaId: subAreaId as SubAreaId,
      role,
      ...(monthlyCents > 0 ? { monthlyCents } : {}),
      ...(otraPersona ? { confirmNewPerson: true } : {}),
    });
    setBusy(false);

    if (!result.ok) {
      /* El motivo importa: el más común va a ser «falta el sueldo», y un «no se
         pudo» a secas manda a alguien a buscar en el sitio equivocado. */
      toast.bad(`No se pudo validar la cuenta.${reasonFor(result.error)}`);
      return;
    }

    toast.ok(`${person.displayName} ya puede entrar al portal.`);
    await onDone();
  };

  // Only a superuser may grant company-wide access; an area administrator
  // promoting someone to `super` would be a straightforward escalation out of
  // their own area.
  const roles: readonly Role[] =
    session.role === 'super'
      ? ['empleado', 'lider', 'admin', 'super']
      : ['empleado', 'lider', 'admin'];

  return (
    <li className={styles.pendingCard}>
      <div className={styles.pendingTop}>
        <Avatar name={person.displayName} color="var(--color-warning)" size={38} />
        <span className={styles.pendingText}>
          <span className={styles.pendingName}>{person.displayName}</span>
          <span className={styles.pendingMail}>{person.email}</span>
        </span>
      </div>

      {parecidas.length > 0 && (
        <div className={styles.pendingMatches} role="note">
          <p className={styles.pendingMatchesTitle}>Parece ser alguien que ya tiene ficha:</p>
          <ul className={styles.pendingMatchList}>
            {parecidas.map((match) => (
              <li key={match.personId} className={styles.pendingMatch}>
                <span className={styles.pendingMatchText}>
                  <strong>{match.displayName}</strong>
                  <span>
                    {[match.areaName, match.email.length > 0 ? match.email : 'sin correo', match.reason]
                      .filter((part) => part.length > 0)
                      .join(' · ')}
                  </span>
                </span>
                <Button
                  variant="primary"
                  disabled={merging}
                  onClick={() => {
                    void merge(match);
                  }}
                >
                  {merging ? 'Uniendo…' : 'Es la misma persona: unir'}
                </Button>
              </li>
            ))}
          </ul>
          <label className={styles.pendingMatchConfirm}>
            <input
              type="checkbox"
              checked={otraPersona}
              onChange={(event) => {
                setOtraPersona(event.target.checked);
              }}
            />
            Es otra persona: darla de alta aparte
          </label>
        </div>
      )}

      <div className={styles.pendingForm}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Sub área</span>
          <select
            className={styles.select}
            value={subAreaId}
            onChange={(event) => {
              setSubAreaId(event.target.value);
            }}
          >
            <option value="">Elige…</option>
            {subAreas.map((sa) => (
              <option key={sa.id} value={sa.id}>
                {sa.name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Rol</span>
          <select
            className={styles.select}
            value={role}
            onChange={(event) => {
              setRole(event.target.value as Role);
            }}
          >
            {roles.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABEL[option]}
              </option>
            ))}
          </select>
        </label>

        {/*
          El sueldo, sólo si falta y sólo a quien puede escribirlo.
          Es donde el campo pasó a ser obligatorio: el alta ya no lo exige porque
          quien captura no tiene por qué ver sueldos, así que validar sí. Si quien
          capturó pudo ponerlo, esto no aparece — pedir dos veces el dato más
          sensible de la base es una forma de que acabe escrito en un post-it.
        */}
        {faltaSueldo && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Sueldo autorizado</span>
            <input
              className={styles.select}
              inputMode="decimal"
              placeholder="18500"
              value={monthlySalary}
              onChange={(event) => {
                setMonthlySalary(event.target.value);
              }}
            />
          </label>
        )}

        <button
          type="button"
          className={styles.approveButton}
          disabled={
            subAreaId.length === 0 ||
            busy ||
            (faltaSueldo && monthlyCents <= 0) ||
            (parecidas.length > 0 && !otraPersona)
          }
          onClick={() => {
            void validate();
          }}
        >
          {busy ? 'Validando…' : faltaSueldo && monthlyCents <= 0 ? 'Falta el sueldo' : 'Validar cuenta'}
        </button>
      </div>
    </li>
  );
};

/**
 * Lo que se enseña cuando el filtro no encontró nada.
 *
 * Existe porque una lista vacía por un filtro y una lista vacía porque el área no
 * tiene a nadie **se veían idénticas**, y la copia que salía era la segunda:
 * buscar «test3» contestaba «Sin personas activas», que es una afirmación sobre la
 * empresa y no sobre lo que acabas de teclear. Quien la leyera podría concluir que
 * se le vació el padrón.
 *
 * Lleva la salida encima, y por eso es un componente y no una cadena: el momento
 * en que hace falta quitar el filtro es exactamente éste, cuando no queda ninguna
 * fila donde apoyar la vista.
 */
const NoSearchResults = ({
  what,
  onClear,
}: {
  what: string;
  onClear: () => void;
}): ReactNode => (
  <EmptyState
    glyph="⌕"
    title={`Ninguna ${what} coincide con lo que buscas`}
    note="El área sigue completa: lo que está acotando la lista es el filtro de arriba."
    action={
      <button type="button" className={styles.emptyAction} onClick={onClear}>
        Quitar filtro
      </button>
    }
  />
);

const PeopleTab = ({
  people,
  roster,
  subAreas,
  links,
  loading,
  search,
  searching,
  needle,
  onClearSearch,
  onChanged,
}: {
  /** Quién pasa el filtro: **las filas que se listan**, y nada más. */
  people: readonly Person[];
  /**
   * El área completa. Con esto se resuelve quién puede ser líder de alguien.
   *
   * Es la misma separación que documenta `shownPersonIds` arriba, y esta pestaña
   * la seguía sin hacer: los chips de «Líderes asignados» salían de la lista ya
   * filtrada, así que teclear «Adri» dejaba la fila de Adriana sin un solo
   * candidato —los líderes no se llaman Adri— y con ellos desaparecía la única
   * manera de asignarle uno. Buscar a una persona no cambia quién manda en el
   * área.
   */
  roster: readonly Person[];
  subAreas: readonly SubArea[];
  links: readonly LeaderLink[];
  loading: boolean;
  /** El buscador del panel, que cada pestaña coloca encima de sus filas. */
  search: ReactNode;
  /** Si hay filtro puesto, que es lo que separa «no hay» de «no encontré». */
  searching: boolean;
  /** Lo tecleado en el buscador, ya normalizado. Cambiarlo vuelve a la página 1. */
  needle: string;
  onClearSearch: () => void;
  onChanged: () => Promise<void>;
}): ReactNode => {
  const openPerson = usePersonLink();
  const toast = useToast();
  const session = useSession();
  /* El área que toca mirar: la propia, o la enfocada si su alcance es la
     empresa entera. Ver `AreaFocusProvider`. */
  const areaId = useScopedAreaId(session.areaId);
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<SubAreaId | 'todas'>('todas');

  /*
   * El conteo de contactos se pide por el padrón, no por lo listado.
   *
   * Colgado de `people` la clave cambiaba con cada tecla, así que teclear un
   * nombre disparaba una tanda nueva de lecturas y, mientras llegaba, la
   * advertencia «tiene menos de dos contactos» desaparecía de la fila que se
   * acababa de buscar. El padrón no cambia al buscar, y la respuesta tampoco.
   */
  const contacts = useQuery({
    queryKey: ['admin', 'emergencyContacts', roster.map((person) => person.id).join(',')],
    enabled: roster.length > 0,
    queryFn: async () => {
      const result = await Promise.all(
        roster.map(async (person) => {
          const contactsForPerson = await getServices().contacts.listFor(person.id);
          return [person.id, contactsForPerson.ok ? contactsForPerson.value.length : 0] as const;
        }),
      );
      return new Map(result);
    },
  });

  const visible = people.filter((p) => filter === 'todas' || p.subAreaId === filter);
  /*
   * De diez en diez. Creación de contenidos tiene 134 personas, y cada renglón
   * trae su sub área y sus líderes: en fila eran más de diez mil píxeles.
   *
   * Los chips de arriba siguen contando el área entera, no la página: rotulan
   * cuánta gente hay, no cuánta se está viendo.
   */
  const pager = usePagination(visible, `${filter}|${needle}`);

  if (loading) return <Spinner label="Cargando personas" />;
  if (people.length === 0) {
    return (
      <div>
        {/* El buscador se queda: sin él no hay dónde borrar lo tecleado, y era
            justamente la pantalla desde la que no se podía salir. */}
        {search}
        {searching ? (
          <NoSearchResults what="persona" onClear={onClearSearch} />
        ) : (
          <EmptyState glyph="·" title="Sin personas activas" />
        )}
      </div>
    );
  }


  /*
   * Candidates to lead: the people who hold the role of leader. Nothing else.
   *
   * This list has now been wrong twice, in opposite directions, and both errors
   * came from describing a leader as something other than a leader.
   *
   * `p.role !== 'empleado'` let the administrator and the superuser through —
   * configuration roles that never sign an absence — so a superuser found their
   * own account offered as somebody's leader. Assigning one was not cosmetic: it
   * writes a link to a person who can never approve, and the request waits in a
   * queue nobody can clear.
   *
   * Replacing it with «holds `aprobar`» read the live permission matrix, which
   * sounded principled and was worse: that matrix is editable from Configuración
   * global, so one edit turned all thirty-seven people in the area into leader
   * candidates. A UI permission is not the same fact as a role.
   *
   * El rol es el hecho, y es como define «líder» todo lo demás de esta pantalla
   * —el padrón, el conteo y «Líderes» al lado—. Los cuatro sitios preguntaban lo
   * mismo con un `role === 'lider'` copiado; ahora preguntan a `leadsTeams`, que
   * es donde está escrito el porqué y la historia de los dos intentos de arriba.
   *
   * `global` entró ahí el 28 sep 2026: dejó de ser cierto que ese rol no firma.
   */
  /*
   * Y quien ya lidera a alguien, o administra el área (22 oct 2026).
   *
   * Desde que quien administra el área queda de líder por omisión, hay vínculos
   * hacia `admin`, y también los hay hacia un `super` —Paulo, de Mariana, desde
   * que le cambiaron el rol—. Con sólo `leadsTeams` esos líderes no salían en la
   * fila: Mariana se veía sin líder marcado con la etiqueta «Una firma» al lado.
   * Un vínculo que existe se enseña; ofrecer a un `super` que no lidera a nadie
   * sigue sin hacerse, por lo que cuenta `leadsTeams`.
   */
  const linkedLeaderIds = new Set(links.map((link) => link.leaderPersonId));
  const candidates = roster.filter(
    (person) =>
      leadsTeams(person.role) || person.role === 'admin' || linkedLeaderIds.has(person.id),
  );

  const changeSubArea = async (person: Person, subAreaId: string): Promise<void> => {
    const result = await getServices().people.setSubArea(
      person.id,
      subAreaId.length === 0 ? null : (subAreaId as SubAreaId),
    );
    if (!result.ok) {
      toast.bad('No se pudo cambiar la sub área.');
      return;
    }
    toast.ok(`${person.displayName} movido de sub área.`);
    await onChanged();
  };

  /**
   * Assign or remove a leader, with the chip flipping before the server answers.
   *
   * The write is a callable followed by two refetches, so the honest round trip is
   * long enough that the chip looked inert and people pressed it again — and a
   * second press on a link that is already being written is a different request,
   * not a repeat.
   *
   * Optimism is safe here in a way it is not for an approval: the link is two ids,
   * the outcome is binary, and there is no server-assigned value to reconcile — no
   * folio, no computed day count. So the cache takes the change immediately and,
   * if the server refuses, the previous list goes back exactly as it was. The
   * refetch that follows a success is what makes the server the last word.
   */
  const toggleLeader = async (person: Person, leader: Person, on: boolean): Promise<void> => {
    const service = getServices().leaders;
    /* Si era el único, la persona se queda sin quien le firme. Se puede —lo
       aprueba quien administre el área— pero tiene que decirse, y decirse antes:
       el «Personas sin líder» de Inicio lo cuenta al día siguiente. */
    const lastOne = on && links.filter((l) => l.personId === person.id).length === 1;
    const key = linksKeyFor(areaId);
    const previous = queryClient.getQueryData<readonly PersonLeaderLink[]>(key);

    queryClient.setQueryData<readonly PersonLeaderLink[]>(key, (current = []) =>
      on
        ? current.filter(
            (link) => !(link.personId === person.id && link.leaderPersonId === leader.id),
          )
        : [...current, provisionalLink(person.id, leader.id, areaId)],
    );

    const result = on
      ? await service.unlink(person.id, leader.id)
      : await service.link(person.id, leader.id);

    if (!result.ok) {
      queryClient.setQueryData(key, previous);
      /* La razón del servidor, no «no se pudo»: este botón la tuvo tapada mientras
         el servidor se negaba a quitar al último líder, y la pantalla no daba
         forma de averiguar por qué. */
      toast.bad(`No se pudo actualizar el liderazgo.${reasonFor(result.error)}`);
      return;
    }
    if (lastOne) {
      toast.warn(
        `${person.displayName} se quedó sin ese líder: pasa a quien administra el área, que queda como su líder.`,
      );
    } else {
      toast.ok(
        on
          ? `${leader.displayName} ya no aprueba a ${person.displayName}.`
          : `${leader.displayName} ahora aprueba a ${person.displayName}.`,
      );
    }
    await onChanged();
  };

  return (
    <div>
      <div className={styles.subFilters} role="group" aria-label="Filtrar por sub área">
        <button
          type="button"
          aria-pressed={filter === 'todas'}
          className={cx(styles.subFilter, filter === 'todas' && styles.subFilterActive)}
          onClick={() => {
            setFilter('todas');
          }}
        >
          Todas
          <span className={styles.subFilterCount}>{people.length}</span>
        </button>
        {subAreas.map((sa) => (
          <button
            key={sa.id}
            type="button"
            aria-pressed={filter === sa.id}
            className={cx(styles.subFilter, filter === sa.id && styles.subFilterActive)}
            style={cssVars({ '--scope-color': subAreaColor(sa) })}
            onClick={() => {
              setFilter(sa.id);
            }}
          >
            {sa.name}
            <span className={styles.subFilterCount}>
              {people.filter((p) => p.subAreaId === sa.id).length}
            </span>
          </button>
        ))}
      </div>

      {search}

      {visible.length === 0 ? (
        <div className={styles.emptyRow}>Nadie asignado a esa sub área todavía.</div>
      ) : (
        <ul className={styles.rows}>
          {pager.items.map((person) => {
            const sa = subAreas.find((s) => s.id === person.subAreaId);
            const myLeaders = links.filter((l) => l.personId === person.id);
            const dual = myLeaders.length >= 2;
            const contactCount = contacts.data?.get(person.id) ?? MINIMUM_EMERGENCY_CONTACTS;

            return (
              <li key={person.id} className={styles.staffRow}>
                <div className={styles.staffIdentity}>
                  <Avatar
                    name={person.displayName}
                    color={subAreaColor(sa)}
                    size={38}
                    onOpen={openPerson(person.id)}
                  />
                  <span className={styles.staffText}>
                    <span className={styles.staffName}>{person.displayName}</span>
                    <span className={styles.staffRole}>{ROLE_LABEL[person.role]}</span>
                    {contactCount < MINIMUM_EMERGENCY_CONTACTS && (
                      <span className={styles.staffWarning}>
                        Tiene menos de dos contactos: pídele completarlos en su perfil.
                      </span>
                    )}
                  </span>
                </div>

                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Sub área</span>
                  <select
                    className={styles.select}
                    value={person.subAreaId ?? ''}
                    aria-label={`Sub área de ${person.displayName}`}
                    onChange={(event) => {
                      void changeSubArea(person, event.target.value);
                    }}
                  >
                    <option value="">Sin sub área</option>
                    {subAreas.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className={styles.staffLeads}>
                  <span className={styles.fieldLabel}>Líderes asignados</span>
                  <div className={styles.leadChips}>
                    {candidates
                    .filter((candidate) => candidate.id !== person.id)
                      .map((candidate) => {
                        const on = myLeaders.some((l) => l.leaderPersonId === candidate.id);
                        const candidateSubArea = subAreas.find(
                          (subArea) => subArea.id === candidate.subAreaId,
                        );
                        return (
                          <button
                            key={candidate.id}
                            type="button"
                            aria-pressed={on}
                            className={cx(styles.leadChip, on && styles.leadChipOn)}
                            onClick={() => {
                              void toggleLeader(person, candidate, on);
                            }}
                          >
                            {candidate.displayName.split(' ')[0]} ·{' '}
                            {candidateSubArea?.name ?? 'Área'}
                          </button>
                        );
                      })}
                  </div>

                  {/* The rule that surprises people, so it is stated on the row
                      rather than left to be discovered at approval time. */}
                  {dual && (
                    <span className={styles.signNote}>
                      Con dos líderes, sus solicitudes necesitan el visto bueno de ambos.
                    </span>
                  )}
                </div>

                <div className={styles.staffTagCell}>
                  {myLeaders.length === 0 ? (
                    <Chip background="var(--color-danger-bg)" foreground="var(--color-danger-ink)">
                      Sin líder
                    </Chip>
                  ) : dual ? (
                    <Chip background="var(--color-violet-bg)" foreground="var(--color-violet-ink)">
                      Doble visto bueno
                    </Chip>
                  ) : (
                    <Chip background="var(--color-success-bg)" foreground="var(--color-success-ink)">
                      Una firma
                    </Chip>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination pager={pager} noun="personas" />
    </div>
  );
};

const SubAreasTab = ({
  subAreas,
  people,
  search,
  searching,
  onClearSearch,
  onChanged,
}: {
  subAreas: readonly SubArea[];
  people: readonly Person[];
  search: ReactNode;
  searching: boolean;
  onClearSearch: () => void;
  onChanged: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  /* El área que toca mirar: la propia, o la enfocada si su alcance es la
     empresa entera. Ver `AreaFocusProvider`. */
  const areaId = useScopedAreaId(session.areaId);
  const toast = useToast();
  const [newName, setNewName] = useState('');
  const [newMax, setNewMax] = useState('2');

  const create = async (): Promise<void> => {
    if (newName.trim().length === 0) return;

    const result = await getServices().organization.createSubArea({
      areaId: areaId,
      name: newName.trim(),
      maxConcurrentOut: Math.max(MIN_CONCURRENT_OUT, Number(newMax) || MIN_CONCURRENT_OUT),
    });

    if (!result.ok) {
      toast.bad('No se pudo crear la sub área.');
      return;
    }
    toast.ok(`Sub área "${newName.trim()}" creada.`);
    setNewName('');
    await onChanged();
  };

  /*
   * El tope no baja de uno, y no es una preferencia de forma.
   *
   * `assessOverlap` marca un día cuando `peopleOut >= cap`. Con el tope en cero eso
   * se cumple **siempre**, incluso con la sub área entera presente: toda solicitud
   * saldría del asistente avisando de un traslape que no existe, y las ventanas
   * alternativas que se ofrecen tras un rechazo no encontrarían ni una libre. Como
   * el tope es advertencia y no candado, el efecto no es prohibir vacaciones —es
   * peor— sino enseñarle a todo el mundo a ignorar el aviso.
   *
   * Un cero tampoco expresa nada que haga falta decir: «aquí no puede faltar
   * nadie» no es una regla que este portal aplique, porque no bloquea.
   *
   * El alta ya forzaba el mínimo a uno; esto era la otra mitad de la misma regla,
   * escrita distinto — y el servidor aceptaba las dos.
   */
  const setCap = async (id: SubAreaId, value: number): Promise<void> => {
    await getServices().organization.updateSubArea(id, {
      maxConcurrentOut: Math.max(MIN_CONCURRENT_OUT, value),
    });
    await onChanged();
  };

  /* El nombre, sin eliminarla y volverla a crear. El servidor rechaza uno que ya
     use otra sub área del área y pone el nuevo en las ausencias que la nombran. */
  const rename = async (sa: SubArea, name: string): Promise<boolean> => {
    const result = await getServices().organization.updateSubArea(sa.id, { name });
    if (!result.ok) {
      toast.bad(`No se pudo renombrar.${reasonFor(result.error)}`);
      return false;
    }
    toast.ok(`${sa.name} ahora se llama ${name}.`);
    await onChanged();
    return true;
  };

  const setLead = async (id: SubAreaId, personId: string): Promise<void> => {
    await getServices().organization.updateSubArea(id, {
      leadPersonId: personId.length === 0 ? null : (personId as PersonId),
    });
    toast.ok('Responsable actualizado.');
    await onChanged();
  };

  const remove = async (sa: SubArea): Promise<void> => {
    const occupied = people.filter((p) => p.subAreaId === sa.id).length;
    if (occupied > 0) {
      toast.warn(
        `${sa.name} todavía tiene ${String(occupied)} personas. Muévelas antes de eliminarla.`,
      );
      return;
    }
    const result = await getServices().organization.deleteSubArea(sa.id);
    if (!result.ok) {
      toast.bad('No se pudo eliminar la sub área.');
      return;
    }
    toast.info(`${sa.name} eliminada.`);
    await onChanged();
  };

  return (
    <div>
      {search}

      {/* Esta pestaña no tenía estado vacío ninguno: filtrar a cero dejaba un
          `<ul>` sin filas y nada que lo explicara. Un panel en blanco es la forma
          en que una pantalla dice «no sé qué pasó». */}
      {subAreas.length === 0 &&
        (searching ? (
          <NoSearchResults what="sub área" onClear={onClearSearch} />
        ) : (
          <EmptyState
            glyph="◇"
            title="Sin sub áreas todavía"
            note="Crea la primera abajo: son las que agrupan a la gente y fijan cuántos pueden estar fuera a la vez."
          />
        ))}

      {/* Cuadrícula y no lista: la fila tenía el nombre a la izquierda, el
          responsable en medio y el tope a la derecha, con medio ancho vacío entre
          ellos en cuanto la pantalla pasaba de 1000px. En tarjetas caben dos o tres
          por renglón y cada sub área se lee entera de un vistazo. */}
      <ul className={styles.subGrid}>
        {subAreas.map((sa) => {
          const count = people.filter((p) => p.subAreaId === sa.id).length;
          return (
            <li
              key={sa.id}
              className={styles.subCard}
              style={cssVars({ '--sub-color': subAreaColor(sa) })}
            >
              <span className={styles.subDot} aria-hidden="true" />
              <SubAreaName
                name={sa.name}
                count={count}
                onRename={(next) => rename(sa, next)}
              />

              {/*
                «Responsable» y no «Líder», que es como se llamaba.

                Son dos cosas distintas que se llamaban igual, y la confusión se
                veía en pantalla: alguien podía ser «líder» de una sub área y llevar
                al lado la etiqueta roja «Sin líder», porque nadie firma por él.
                Esto es quién responde por la sub área —capacidad, cobertura,
                organigrama—; quién firma unas vacaciones vive en `personLeaders`,
                se asigna persona por persona, y es lo único que enruta una
                solicitud. Medido en las dos capas antes de renombrar: ni el cliente
                ni el servidor leen `leadPersonId` para decidir nada de una ausencia.
              */}
              <label className={styles.subLeadField}>
                <span className={styles.subLeadHead}>
                  <span className={styles.subFieldLabel}>Responsable</span>
                  {sa.leadPersonId === null && (
                    <Chip
                      background="var(--color-warning-bg)"
                      foreground="var(--color-warning-ink)"
                    >
                      Por asignar
                    </Chip>
                  )}
                </span>
                <select
                  className={styles.select}
                  value={sa.leadPersonId ?? ''}
                  aria-label={`Responsable de ${sa.name}`}
                  onChange={(event) => {
                    void setLead(sa.id, event.target.value);
                  }}
                >
                  <option value="">Por asignar</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </label>

              <span className={styles.subFooter}>
                <span className={styles.capCell}>
                  <span className={styles.subFieldLabel}>Máx. fuera</span>
                  <span className={styles.stepper}>
                    {/* Apagado en el mínimo en vez de silenciosamente inerte: un
                        botón que se pulsa y no mueve el número se lee como roto. */}
                    <button
                      type="button"
                      className={styles.stepperButton}
                      disabled={sa.maxConcurrentOut <= MIN_CONCURRENT_OUT}
                      aria-label={
                        sa.maxConcurrentOut <= MIN_CONCURRENT_OUT
                          ? `El tope de ${sa.name} no puede bajar de ${String(MIN_CONCURRENT_OUT)}`
                          : `Reducir el tope de ${sa.name}`
                      }
                      onClick={() => {
                        void setCap(sa.id, sa.maxConcurrentOut - 1);
                      }}
                    >
                      −
                    </button>
                    <span className={styles.stepperValue}>{sa.maxConcurrentOut}</span>
                    <button
                      type="button"
                      className={styles.stepperButton}
                      aria-label={`Aumentar el tope de ${sa.name}`}
                      onClick={() => {
                        void setCap(sa.id, sa.maxConcurrentOut + 1);
                      }}
                    >
                      +
                    </button>
                  </span>
                </span>
                <button
                  type="button"
                  className={styles.removeLink}
                  onClick={() => {
                    void remove(sa);
                  }}
                >
                  Eliminar
                </button>
              </span>
            </li>
          );
        })}
      </ul>

      <div className={styles.createRow}>
        <label className={styles.createField}>
          <span className={styles.createLabel}>Nueva sub área</span>
          <input
            type="text"
            className={styles.input}
            placeholder="Ej. Data Engineering"
            value={newName}
            onChange={(event) => {
              setNewName(event.target.value);
            }}
          />
        </label>
        <label className={cx(styles.createField, styles.createFieldNarrow)}>
          <span className={styles.createLabel}>Máximo fuera a la vez</span>
          <input
            type="number"
            min={1}
            max={50}
            className={styles.input}
            value={newMax}
            onChange={(event) => {
              setNewMax(event.target.value);
            }}
          />
        </label>
        <button
          type="button"
          className={styles.createButton}
          disabled={newName.trim().length === 0}
          onClick={() => {
            void create();
          }}
        >
          Agregar
        </button>
      </div>
    </div>
  );
};

/** Cómo se nombra, en la lista de doble liderazgo, a un líder que el área ya no tiene. */
const FOREIGN_LEADER = 'un líder que ya no es del área';

/**
 * Líderes — who may approve in the area, and who needs two signatures.
 *
 * The tab manages the role rather than just listing it, because the two
 * mutations here are the ones that break approvals when they are wrong.
 * Promoting someone is cheap; removing them strands everybody who reported to
 * them, so that path says how many people it is about to leave without a leader
 * instead of quietly succeeding.
 */
const LeadersTab = ({
  people,
  shownPersonIds,
  links,
  subAreas,
  dualOn,
  search,
  onClearSearch,
  onChanged,
}: {
  /** El área completa: con esto se resuelven nombres, roles y candidatos. */
  people: readonly Person[];
  /** Quién pasa el filtro. Decide **qué filas se enseñan** y nada más. */
  shownPersonIds: ReadonlySet<PersonId>;
  links: readonly LeaderLink[];
  subAreas: readonly SubArea[];
  dualOn: boolean;
  search: ReactNode;
  /*
   * Sin `searching`, y no es un olvido: con el filtro apagado `shownPersonIds`
   * contiene a todo el mundo, así que `shownLeaderIds` es `leaderIds` y la rama de
   * «ninguno coincide» no se puede alcanzar. Pedir la bandera sería pedir un dato
   * que ya está implícito, y dejarla sin usar invita a que alguien la lea como si
   * significara algo.
   */
  onClearSearch: () => void;
  onChanged: () => Promise<void>;
}): ReactNode => {
  const openPerson = usePersonLink();
  const toast = useToast();
  const [candidate, setCandidate] = useState('');
  const [busy, setBusy] = useState(false);

  /*
   * Leadership is the role, not the links.
   *
   * Deriving it from who has reports made a freshly named leader invisible —
   * the action succeeded and the list did not move — and it is also the wrong
   * rule: the role is what lets someone sign, and someone can hold it before
   * anybody is assigned to them. The links only supply the head count.
   *
   * Era la quinta copia de `role === 'lider'` en esta pantalla, y la que se
   * escapó cuando las otras cuatro pasaron a `leadsTeams`: el administrador
   * global que dirige a 22 personas seguía desapareciendo de esta pestaña.
   */
  const leaderIds = people.filter((p) => leadsTeams(p.role)).map((p) => p.id);
  const byId = new Map(people.map((p) => [p.id, p]));

  /* Las filas que se enseñan. `leaderIds` sigue siendo el área entera, porque de
     él salen el conteo, los candidatos y la respuesta a «¿hay líderes?». */
  const shownLeaderIds = leaderIds.filter((id) => shownPersonIds.has(id));
  const subAreaName = (id: SubAreaId | null): string =>
    id === null ? 'Sin sub área' : (subAreas.find((s) => s.id === id)?.name ?? 'Sin sub área');

  /* Anyone in the area who is not already a leader and can hold the role. */
  const candidates = people.filter(
    (p) => !leaderIds.includes(p.id) && p.role !== 'admin' && p.role !== 'super',
  );

  const multiLead = people
    .map((person) => ({
      person,
      leaders: links
        .filter((l) => l.personId === person.id)
        /*
         * Un vínculo cuyo líder no está en el padrón del área. Decía «Desconocido»,
         * que no dice qué pasa ni qué hacer: es alguien que ya no es del área —lo
         * trasladaron, o el vínculo se cargó así— y que desde donde está no puede
         * firmarle. El traslado y la baja ya se llevan estos vínculos; los que
         * queden son anteriores al 13 oct 2026.
         */
        .map((l) => byId.get(l.leaderPersonId)?.displayName ?? FOREIGN_LEADER),
    }))
    .filter((row) => row.leaders.length >= 2);

  /*
   * Las dos listas de esta pestaña, de diez en diez.
   *
   * La de líderes vuelve al principio cuando cambia a quién deja pasar el
   * buscador —el tamaño de `shownPersonIds` es lo que se mueve al teclear—. La
   * de doble liderazgo no se filtra, así que sólo se ajusta a lo que haya.
   */
  const leadersPager = usePagination(shownLeaderIds, String(shownPersonIds.size));
  const multiPager = usePagination(multiLead);

  const promote = async (): Promise<void> => {
    const person = people.find((p) => p.id === candidate);
    if (person === undefined) {
      toast.info('Elige a una persona del área para hacerla líder.');
      return;
    }
    setBusy(true);
    const services = getServices();
    const result = await services.people.setRole(person.id, 'lider');

    if (result.ok) {
      // A sub area with nobody responsible gets them — the commonest reason to
      // promote someone is that their own sub area has no lead.
      const ownSubArea = subAreas.find((s) => s.id === person.subAreaId);
      if (ownSubArea !== undefined && ownSubArea.leadPersonId === null) {
        await services.organization.updateSubArea(ownSubArea.id, { leadPersonId: person.id });
      }
    }

    setBusy(false);
    if (!result.ok) {
      toast.bad('No se pudo nombrar líder.');
      return;
    }
    toast.ok(
      `${person.displayName} ahora es líder. Asígnale personas desde «Líderes asignados».`,
    );
    setCandidate('');
    await onChanged();
  };

  /*
   * Quitar de líderes: una sola llamada, y el servidor decide.
   *
   * Antes esto desvinculaba a cada reporte desde aquí, en un bucle que **no
   * miraba el resultado de ninguna llamada**: un `unlink` rechazado —por reglas,
   * por red— dejaba el vínculo en pie y el rol cambiaba igual, con el toast
   * diciendo que había ido bien. El estado que producía es exactamente el que hay
   * hoy en producción: alguien firmado por quien ya no puede aprobar.
   *
   * Ahora `setPersonRole` se planta si la persona tiene gente a cargo, y suelta él
   * mismo las sub áreas que lidere. Su mensaje dice cuántas personas son y dónde
   * reasignarlas, así que se muestra en vez de sustituirlo por «no se pudo».
   */
  const demote = async (leader: Person, reportCount: number): Promise<void> => {
    setBusy(true);
    const result = await getServices().people.setRole(leader.id, 'empleado');
    setBusy(false);

    if (!result.ok) {
      toast.bad(`No se pudo quitar de líderes.${reasonFor(result.error)}`);
      return;
    }

    toast.warn(
      reportCount === 0
        ? `${leader.displayName} ya no es líder. No tenía personas a cargo.`
        : `${leader.displayName} ya no es líder.`,
    );
    await onChanged();
  };

  return (
    <div>
      <div className={styles.leadRuleHead}>
        <Chip
          background={dualOn ? 'var(--color-success-bg)' : 'var(--color-surface-subtle-alt)'}
          foreground={dualOn ? 'var(--color-success-ink)' : 'var(--color-text-tertiary-alt)'}
        >
          {dualOn ? 'Activo' : 'Inactivo'}
        </Chip>
      </div>
      <p className={styles.leadRule}>
        {dualOn
          ? 'Con más de 2 líderes en el área, quien trabaja en proyectos de varios líderes necesita dos firmas.'
          : 'El doble visto bueno está apagado. El superusuario lo controla en Features y módulos.'}
      </p>

      {search}

      {leaderIds.length === 0 ? (
        <EmptyState
          glyph="★"
          title="Sin líderes asignados"
          note="Mientras no haya ninguno, las solicitudes del área las firma quien la administra."
        />
      ) : shownLeaderIds.length === 0 ? (
        /* Hay líderes; ninguno se llama así. Decirlo con la copia de arriba
           afirmaría que el área se quedó sin quién firme, que es otra cosa y
           bastante peor. */
        <NoSearchResults what="persona con rol de líder" onClear={onClearSearch} />
      ) : (
        <ul className={styles.rows}>
          {leadersPager.items.map((id) => {
            const leader = byId.get(id);
            const reports = links.filter((l) => l.leaderPersonId === id);
            // The project a leader signs off on, taken from their own links.
            const project = reports.find((r) => r.projectLabel.length > 0)?.projectLabel;

            return (
              <li key={id} className={styles.leaderRow}>
                <Avatar name={leader?.displayName ?? '—'} color="var(--color-brand)" size={34} />
                <span className={styles.leaderText}>
                  <span className={styles.leaderName}>{leader?.displayName ?? 'Desconocido'}</span>
                  <span className={styles.leaderMeta}>
                    {leader === undefined
                      ? 'Fuera del área'
                      : `${subAreaName(leader.subAreaId)}${
                          project === undefined ? '' : ` · ${project}`
                        }`}
                  </span>
                </span>
                <Chip background="var(--color-brand-bg-alt)" foreground="var(--color-brand-ink)">
                  {reports.length} a cargo
                </Chip>
                {leader !== undefined && (
                  <button
                    type="button"
                    className={styles.leaderRemove}
                    disabled={busy}
                    onClick={() => {
                      void demote(leader, reports.length);
                    }}
                  >
                    Quitar
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Pagination pager={leadersPager} noun="líderes" />

      <div className={styles.promote}>
        <label className={styles.promoteField}>
          <span className={styles.promoteLabel}>Nombrar nuevo líder</span>
          <select
            className={styles.select}
            value={candidate}
            disabled={busy || candidates.length === 0}
            onChange={(event) => {
              setCandidate(event.target.value);
            }}
          >
            <option value="">
              {candidates.length === 0 ? 'Todo el área ya es líder' : 'Elige a una persona…'}
            </option>
            {candidates.map((person) => (
              <option key={person.id} value={person.id}>
                {person.displayName} · {subAreaName(person.subAreaId)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={styles.promoteButton}
          disabled={busy || candidate.length === 0}
          onClick={() => {
            void promote();
          }}
        >
          Hacer líder
        </button>
      </div>

      {multiLead.length > 0 && (
        <div className={styles.multiSection}>
          <div className={styles.multiTitle}>
            {multiLead.length}{' '}
            {multiLead.length === 1
              ? 'persona trabaja para más de un líder'
              : 'personas trabajan para más de un líder'}
          </div>
          <ul className={styles.multiList}>
            {multiPager.items.map((row) => (
              <li key={row.person.id} className={styles.multiRow}>
                <Avatar
                  name={row.person.displayName}
                  color="var(--color-brand)"
                  size={30}
                  onOpen={openPerson(row.person.id)}
                />
                <span className={styles.multiName}>{row.person.displayName}</span>
                <span className={styles.multiLeads}>{row.leaders.join('  +  ')}</span>
              </li>
            ))}
          </ul>

          <Pagination pager={multiPager} noun="personas" />
        </div>
      )}
    </div>
  );
};
